'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Calendar, Star, AlertTriangle, CheckCircle, XCircle, ChevronRight, MoreHorizontal, RefreshCw, Download } from 'lucide-react';

const RevenueChart = dynamic(() => import('./RevenueChart'), { ssr: false });
const AppointmentStatusChart = dynamic(() => import('./AppointmentStatusChart'), { ssr: false });
const PaymentPieChart = dynamic(() => import('./PaymentPieChart'), { ssr: false });

const recentAppointments = [
  { id: 'apt-0425', customer: 'Shanice Paulina', vehicle: 'Honda Civic · GHI-9012', service: 'Outside + Inside', type: 'mobile', status: 'inprogress', time: '09:30', employee: 'Jayden R.', price: 50 },
  { id: 'apt-0424', customer: 'Renaldo Cijntje', vehicle: 'Toyota Prado · JKL-3456', service: 'Full Interior Detail', type: 'location', status: 'confirmed', time: '11:00', employee: 'Marcus A.', price: 125 },
  { id: 'apt-0423', customer: 'Lisette van Dijk', vehicle: 'Hyundai Tucson · MNO-7890', service: 'Outside Wash', type: 'mobile', status: 'assigned', time: '12:15', employee: 'Jayden R.', price: 30 },
  { id: 'apt-0422', customer: 'Mikhail Albertus', vehicle: 'Nissan X-Trail · PQR-1234', service: 'Wax Protection', type: 'location', status: 'new', time: '14:00', employee: '—', price: 75 },
  { id: 'apt-0421', customer: 'Keisha Martina', vehicle: 'Toyota Corolla · ABC-1234', service: 'Outside + Inside', type: 'mobile', status: 'confirmed', time: '10:45', employee: 'Jayden R.', price: 50 },
  { id: 'apt-0420', customer: 'Darnell Winklaar', vehicle: 'Ford Ranger · STU-5678', service: 'Engine Bay Clean', type: 'location', status: 'completed', time: '08:00', employee: 'Marcus A.', price: 50 },
  { id: 'apt-0419', customer: 'Clarissa Römer', vehicle: 'Kia Sportage · VWX-9012', service: 'Seat Shampoo', type: 'location', status: 'completed', time: '07:30', employee: 'Marcus A.', price: 75 },
  { id: 'apt-0418', customer: 'Fabian Pieters', vehicle: 'Suzuki Swift · YZA-3456', service: 'Outside Wash', type: 'mobile', status: 'noshow', time: '09:00', employee: 'Jayden R.', price: 30 },
];

const employees = [
  { id: 'emp-001', name: 'Jayden Rosario', role: 'Mobile Crew', jobsToday: 5, completed: 3, revenue: 280, rating: 4.9 },
  { id: 'emp-002', name: 'Marcus Albertus', role: 'Location Crew', jobsToday: 6, completed: 5, revenue: 420, rating: 4.8 },
  { id: 'emp-003', name: 'Tyrone Cijntje', role: 'Mobile Crew', jobsToday: 4, completed: 4, revenue: 240, rating: 4.7 },
  { id: 'emp-004', name: 'Naomi Römer', role: 'Location Crew', jobsToday: 3, completed: 2, revenue: 175, rating: 4.6 },
];

const topServices = [
  { id: 'svc-rank-001', name: 'Outside + Inside Wash', bookings: 142, revenue: 7100, trend: 'up' },
  { id: 'svc-rank-002', name: 'Outside Wash Only', bookings: 98, revenue: 2940, trend: 'up' },
  { id: 'svc-rank-003', name: 'Full Interior Detail', bookings: 34, revenue: 4250, trend: 'down' },
  { id: 'svc-rank-004', name: 'Wax Protection', bookings: 28, revenue: 2100, trend: 'up' },
  { id: 'svc-rank-005', name: 'Seat Shampoo', bookings: 22, revenue: 1650, trend: 'down' },
];

const statusBadgeMap: Record<string, string> = {
  new: 'badge-new',
  assigned: 'badge-assigned',
  confirmed: 'badge-confirmed',
  onway: 'badge-onway',
  inprogress: 'badge-inprogress',
  completed: 'badge-completed',
  cancelled: 'badge-cancelled',
  noshow: 'badge-noshow',
};

const statusLabelMap: Record<string, string> = {
  new: 'New',
  assigned: 'Assigned',
  confirmed: 'Confirmed',
  onway: 'On the Way',
  inprogress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  noshow: 'No-show',
};

export default function AdminDashboardContent() {
  const [activeTab, setActiveTab] = useState<'overview' | 'appointments' | 'employees' | 'services'>('overview');

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-extrabold text-dark">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Wednesday, 30 April 2026 · Last updated 2 min ago</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => toast.success('Data refreshed')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-sky-200 text-sm font-semibold text-slate-700 hover:bg-sky-50 transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={() => toast.info('Export report feature coming soon')}
            className="btn-primary text-sm px-5 py-2.5"
          >
            <Download size={14} />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Bento Grid — 8 cards: 4+4 in 2 rows of 4 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-4 gap-4 mb-8">
        {/* Hero: Today's Revenue */}
        <div className="col-span-2 lg:col-span-1 bg-gradient-primary rounded-2xl p-5 text-white shadow-glow">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wide text-white/80">Today&apos;s Revenue</span>
            <TrendingUp size={16} className="text-white/70" />
          </div>
          <p className="text-3xl font-extrabold tabular-nums">1,115 XCG</p>
          <p className="text-xs text-white/70 mt-1">↑ 12% vs yesterday</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Weekly</span>
            <TrendingUp size={14} className="text-green-500" />
          </div>
          <p className="text-2xl font-extrabold text-dark tabular-nums">6,840 XCG</p>
          <p className="text-xs text-muted-foreground mt-1">Mon–Sun this week</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monthly</span>
            <TrendingUp size={14} className="text-green-500" />
          </div>
          <p className="text-2xl font-extrabold text-dark tabular-nums">24,390 XCG</p>
          <p className="text-xs text-muted-foreground mt-1">April 2026</p>
        </div>

        <div className="stat-card border-amber-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pending Pay</span>
            <AlertTriangle size={14} className="text-amber-500" />
          </div>
          <p className="text-2xl font-extrabold text-amber-600 tabular-nums">3</p>
          <p className="text-xs text-amber-600 mt-1 font-medium">Requires confirmation</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Today&apos;s Jobs</span>
            <Calendar size={14} className="text-primary" />
          </div>
          <p className="text-2xl font-extrabold text-dark tabular-nums">18</p>
          <p className="text-xs text-muted-foreground mt-1">12 assigned · 6 pending</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Completed</span>
            <CheckCircle size={14} className="text-green-500" />
          </div>
          <p className="text-2xl font-extrabold text-dark tabular-nums">9</p>
          <p className="text-xs text-muted-foreground mt-1">50% completion rate</p>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Subscribers</span>
            <Star size={14} className="text-violet-500" />
          </div>
          <p className="text-2xl font-extrabold text-dark tabular-nums">34</p>
          <p className="text-xs text-muted-foreground mt-1">250 XCG/mo each</p>
        </div>

        <div className="stat-card border-red-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">No-shows</span>
            <XCircle size={14} className="text-red-500" />
          </div>
          <p className="text-2xl font-extrabold text-red-600 tabular-nums">2</p>
          <p className="text-xs text-red-600 mt-1 font-medium">Today · Follow up</p>
        </div>
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 2xl:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-sky-100 shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-dark">Revenue — Last 7 Days</h3>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-primary inline-block" />Location</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />Mobile</span>
            </div>
          </div>
          <RevenueChart />
        </div>
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card p-5">
          <h3 className="font-bold text-dark mb-4">Payment Methods</h3>
          <PaymentPieChart />
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { label: 'Sentoo', val: '42%', color: 'bg-primary' },
              { label: 'Card', val: '28%', color: 'bg-violet-500' },
              { label: 'Banking', val: '18%', color: 'bg-secondary' },
              { label: 'Cash', val: '12%', color: 'bg-green-500' },
            ].map((item) => (
              <div key={`pay-legend-${item.label}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={`w-2 h-2 rounded-full ${item.color} flex-shrink-0`} />
                {item.label}: <strong className="text-dark">{item.val}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-sky-50 rounded-2xl p-1 mb-6 overflow-x-auto">
        {[
          { id: 'overview', label: 'Appointments' },
          { id: 'employees', label: 'Employees' },
          { id: 'services', label: 'Top Services' },
        ].map((t) => (
          <button
            key={`admin-tab-${t.id}`}
            onClick={() => setActiveTab(t.id as typeof activeTab)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
              activeTab === t.id ? 'bg-white text-dark shadow-sm' : 'text-muted-foreground hover:text-dark'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Appointments table */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-sky-50 flex items-center justify-between">
            <h3 className="font-bold text-dark">Today&apos;s Appointments</h3>
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search customer or plate..."
                className="px-3 py-2 rounded-xl border border-input bg-sky-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 w-48 lg:w-64"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sky-50/60">
                  {['ID', 'Customer', 'Vehicle', 'Service', 'Type', 'Time', 'Employee', 'Status', 'Price', 'Actions'].map((h) => (
                    <th key={`admin-th-${h}`} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recentAppointments.map((apt, i) => (
                  <tr key={apt.id} className={`hover:bg-sky-50/50 transition-colors group ${i < recentAppointments.length - 1 ? 'border-b border-sky-50' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{apt.id}</td>
                    <td className="px-4 py-3 font-semibold text-dark whitespace-nowrap">{apt.customer}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-mono whitespace-nowrap">{apt.vehicle}</td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{apt.service}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${apt.type === 'mobile' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                        {apt.type === 'mobile' ? '🚐' : '📍'} {apt.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-dark tabular-nums">{apt.time}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{apt.employee}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${statusBadgeMap[apt.status] || 'badge-new'}`}>
                        {statusLabelMap[apt.status] || apt.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-extrabold text-primary tabular-nums">{apt.price} XCG</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => toast.info(`Viewing appointment ${apt.id}`)}
                          className="p-1.5 rounded-lg hover:bg-sky-100 text-muted-foreground hover:text-primary transition-colors"
                          title="View appointment details"
                        >
                          <ChevronRight size={14} />
                        </button>
                        <button
                          onClick={() => toast.info(`Editing appointment ${apt.id}`)}
                          className="p-1.5 rounded-lg hover:bg-sky-100 text-muted-foreground hover:text-primary transition-colors"
                          title="Edit appointment"
                        >
                          <MoreHorizontal size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-4 border-t border-sky-50 flex items-center justify-between bg-sky-50/30">
            <p className="text-xs text-muted-foreground">{recentAppointments.length} appointments today</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Total:</span>
              <span className="font-extrabold text-primary tabular-nums">
                {recentAppointments.reduce((s, a) => s + a.price, 0)} XCG
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Employees table */}
      {activeTab === 'employees' && (
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-sky-50">
            <h3 className="font-bold text-dark">Employee Performance — Today</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sky-50/60">
                  {['Employee', 'Role', 'Jobs Today', 'Completed', 'Completion %', 'Revenue Generated', 'Rating'].map((h) => (
                    <th key={`emp-th-${h}`} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, i) => (
                  <tr key={emp.id} className={`hover:bg-sky-50/50 transition-colors ${i < employees.length - 1 ? 'border-b border-sky-50' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {emp.name.charAt(0)}
                        </div>
                        <span className="font-semibold text-dark">{emp.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge ${emp.role.includes('Mobile') ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'}`}>
                        {emp.role}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-dark tabular-nums">{emp.jobsToday}</td>
                    <td className="px-4 py-3 font-semibold text-dark tabular-nums">{emp.completed}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 bg-sky-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-primary rounded-full"
                            style={{ width: `${(emp.completed / emp.jobsToday) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-dark tabular-nums">
                          {Math.round((emp.completed / emp.jobsToday) * 100)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-extrabold text-primary tabular-nums">{emp.revenue} XCG</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-amber-500 font-bold text-sm">
                        ⭐ {emp.rating}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top services table */}
      {activeTab === 'services' && (
        <div className="bg-white rounded-2xl border border-sky-100 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-sky-50">
            <h3 className="font-bold text-dark">Top Services — April 2026</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-sky-50/60">
                  {['Rank', 'Service', 'Bookings', 'Revenue', 'Trend'].map((h) => (
                    <th key={`svc-th-${h}`} className="px-4 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topServices.map((svc, i) => (
                  <tr key={svc.id} className={`hover:bg-sky-50/50 transition-colors ${i < topServices.length - 1 ? 'border-b border-sky-50' : ''}`}>
                    <td className="px-4 py-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${
                        i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-sky-100 text-sky-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-dark">{svc.name}</td>
                    <td className="px-4 py-3 font-semibold text-dark tabular-nums">{svc.bookings}</td>
                    <td className="px-4 py-3 font-extrabold text-primary tabular-nums">{svc.revenue.toLocaleString()} XCG</td>
                    <td className="px-4 py-3">
                      {svc.trend === 'up' ? (
                        <span className="flex items-center gap-1 text-green-600 text-xs font-semibold">
                          <TrendingUp size={14} /> Up
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-500 text-xs font-semibold">
                          <TrendingDown size={14} /> Down
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}